import type {TrendWindow} from '../components/shared/TrendWindowSelect';

export interface TrendWindowBoundary {
    /** Start of the prior half (ms). */
    start: number;
    /** Split point: start of the recent half (ms). */
    recentStart: number;
    /** End of the range (ms). */
    end: number;
}

/**
 * Compute the recent-vs-prior trend-window boundary shared by Barrier Effectiveness
 * and Report Clustering.
 *
 * - `'allTime'` → null (no window; downstream treats it as "no trend signal").
 * - `'midpoint'` → splits the range in half at `(start + end) / 2`.
 * - numeric N → the last N months are "recent", the N before that are "prior", on
 *   calendar-month boundaries.
 *
 * @param fromMs explicit range start (ms) or null to fall back to the data minimum
 * @param toMs explicit range end (ms) or null to fall back to the data maximum
 * @param getTimestamps lazily yields candidate report timestamps in ms (NaN entries
 *        ignored); only invoked when an explicit bound is missing, so a caller can
 *        build the array cheaply without worrying about the common (filter-set) path.
 */
export function computeTrendWindowBoundary(
    window: TrendWindow,
    fromMs: number | null,
    toMs: number | null,
    getTimestamps: () => number[],
): TrendWindowBoundary | null {
    // 'allTime' is semantically "no window": period stats / window-aware values are
    // all null and any overlay band stays off.
    if (window === 'allTime') return null;

    let dataMin = Infinity;
    let dataMax = -Infinity;
    let scanned = false;
    const scan = (): void => {
        if (scanned) return;
        scanned = true;
        for (const t of getTimestamps()) {
            if (isNaN(t)) continue;
            if (t < dataMin) dataMin = t;
            if (t > dataMax) dataMax = t;
        }
    };

    let end = toMs;
    if (end == null || !isFinite(end)) {
        scan();
        end = dataMax;
    }
    if (!isFinite(end)) return null;

    if (window === 'midpoint') {
        let start = fromMs;
        if (start == null || !isFinite(start)) {
            scan();
            start = dataMin;
        }
        if (!isFinite(start) || start >= end) return null;
        return {start, recentStart: (start + end) / 2, end};
    }

    // Calendar-month boundaries. recentStart / start land at midnight of the first
    // recent / band month so the visual divider snaps to a clean date and shares the
    // bucket key with the displayed cell. Data checks use inclusive lower bounds
    // (t >= recentStart / t >= start) and an exclusive upper at the split
    // (t < recentStart) so a boundary-day midnight report counts once, on the recent
    // side. See [[chart-axis-direction-decision]] and [[feedback-cell-key-cutoff-off-by-one]].
    const endDate = new Date(end);
    const yyyy = endDate.getFullYear();
    const mm = endDate.getMonth();
    const recentMonthStart = new Date(yyyy, mm - (window - 1), 1).getTime();
    const priorMonthStart = new Date(yyyy, mm - (2 * window - 1), 1).getTime();
    return {start: priorMonthStart, recentStart: recentMonthStart, end};
}
